import { fromPromise, PromiseActorLogic } from 'xstate';
import {
  Application,
  DisclosureOperation,
  DisclosuresAttribute,
  GeneratedDisclosures,
  Locale,
  MerchantPaymentProduct,
  Order,
  Program,
  VirtualCardIssuanceResponse,
} from '@ads-bread/shared/bread/codecs';
import {
  NamespacePath,
  usd,
  validateOfferProductTypes,
} from '@ads-bread/shared/bread/util';
import { APIFetch } from '../../lib/hooks/apiFetch';
import { toResultResponse } from '../../lib/handlers/base';
import { APPLICATION_STATUS_CODE_ERROR_MAP } from '../../lib/applicationStatus';
import { getApplication } from '../../lib/handlers/get-application';
import { createApplication as createApplicationHandler } from '../../lib/handlers/create-application';
import {
  getLatestApplication,
  getLatestApplications,
} from '../../lib/handlers/get-latest-application';
import { pollVirtualCardIssuance } from '../../lib/handlers/virtual-card-issuance';
import { applicationCheckout as applicationCheckoutHandler } from '../../lib/handlers/application-checkout';
import { applicationPrepareCheckout as applicationPrepareCheckoutHandler } from '../../lib/handlers/application-prepare-checkout';
import { generateDisclosures } from '../../lib/handlers/generate-disclosures';
import { WindowWithDecibel } from '../../lib/analytics/decibel';
import { ApplicationContext } from './applicationMachine';
import {
  getRequiredDisclosureChecksums,
  stringifyShippingDescription,
} from './utils';
import {
  SendApplicationCheckoutParams,
  SendApplicationPrepareCheckoutParams,
  SendCreateApplicationParams,
  SendFetchApplicationParams,
  SendReCreateApplicationParams,
  SendReCreateContingentInStoreApplicationParams,
} from './types/events';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  EvaluateApplicationErrorResult,
  FetchApplicationResult,
  FetchLatestApplicationResult,
  LocateExchangedOfferAndGenerateDisclosuresResult,
  PrepareInStoreApplicationResult,
  PrepareVirtualCardIssuanceResult,
  ReCreateApplicationResult,
  ReCreateInStoreContingentApplicationResult,
} from './types/actors';

declare const window: WindowWithDecibel;

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Actor that resolves the captured status code (and optional experience code)
 * from an upstream application actor result. This is pure and requires no
 * provider closures.
 */
export const getErrorStatusCodeFromApplication = fromPromise<
  EvaluateApplicationErrorResult,
  unknown
>(({ input }) => {
  return new Promise<EvaluateApplicationErrorResult>((resolve) => {
    const applicationEventDoneResult =
      (input as
        | ApplicationPrepareCheckoutResult
        | ApplicationCheckoutResult
        | ReCreateApplicationResult
        | ReCreateInStoreContingentApplicationResult
        | ReCreateApplicationResult) || null;

    const application = applicationEventDoneResult.applicationRes.result;
    const statusCodes = application?.statusCodes || [];
    const experienceCodes = application?.experienceCodes || [];
    const statusCode =
      statusCodes.find(
        (domainReason) => APPLICATION_STATUS_CODE_ERROR_MAP[domainReason]
      ) || '';
    const experienceCode = experienceCodes[0] || '';
    resolve({ statusCode, experienceCode });
  });
});

type FetchApplicationDeps = {
  apiFetch: APIFetch;
};

/**
 * Factory for `fetchApplication`. Requires the API-fetch wrapper.
 * @param deps
 * @returns actor that fetches an application by id
 */
export const createFetchApplication = (
  deps: FetchApplicationDeps
): PromiseActorLogic<FetchApplicationResult, SendFetchApplicationParams> =>
  fromPromise<FetchApplicationResult, SendFetchApplicationParams>(
    async ({ input }) => {
      const { applicationID, resolve, reject } = input;
      try {
        const applicationRes = await getApplication(
          applicationID,
          deps.apiFetch
        );
        return { applicationRes, resolve, reject };
      } catch (error) {
        throw { error, reject };
      }
    }
  );

type FetchLatestApplicationDeps = {
  apiFetch: APIFetch;
};

/**
 * Factory for `fetchLatestApplication`. Requires the API-fetch wrapper.
 * @param deps
 * @returns actor that fetches the latest application for the buyer
 */
export const createFetchLatestApplication = (
  deps: FetchLatestApplicationDeps
): PromiseActorLogic<FetchLatestApplicationResult> =>
  fromPromise<FetchLatestApplicationResult>(async () => {
    try {
      const applicationRes = await getLatestApplication(deps.apiFetch);
      return {
        applicationRes: toResultResponse<Application | null>(applicationRes),
      };
    } catch (error) {
      throw { error };
    }
  });

type CreateApplicationDeps = {
  apiFetch: APIFetch;
  order: Order;
  locale: Locale;
  merchantOrigin: string;
  locationID: string | null;
  cartID: string | null;
  program: Program;
};

/**
 * Factory for `createApplication`. Requires the API-fetch wrapper, the
 * current order/locale, the merchant origin, optional location/cart IDs, and
 * the program policies for disclosure generation.
 * @param deps
 * @returns actor that creates a new application
 */
export const createCreateApplication = (
  deps: CreateApplicationDeps
): PromiseActorLogic<CreateApplicationResult, SendCreateApplicationParams> =>
  fromPromise<CreateApplicationResult, SendCreateApplicationParams>(
    async ({ input }) => {
      try {
        if (!deps.order) {
          throw new Error('Cannot create an application without an order!');
        }

        const fraudAlloyMachineActorRef =
          input.context.stateMachineActorRefs.fraudAlloyMachineActorRef;

        const fraudAlloyContext =
          fraudAlloyMachineActorRef?.getSnapshot().context;

        const decibelSessionId =
          window.decibelInsight?.<string>('getSessionId');

        const disclosures: DisclosuresAttribute =
          deps.program.policies.application.tof.disclosures?.map(
            (disclosure) => ({
              type: disclosure.type,
            })
          ) || [];

        const extensions: Application['extensions'] = [
          ...(deps.locationID && deps.locationID !== ZERO_UUID
            ? [{ key: 'locationID', value: deps.locationID }]
            : []),
          ...(deps.cartID ? [{ key: 'cartID', value: deps.cartID }] : []),
        ];

        const { items } = deps.order;

        const applicationRes = await createApplicationHandler(
          {
            order: {
              ...deps.order,
              items: items
                ? stringifyShippingDescription(items, deps.locale)
                : [],
            },
            retargetingURL: deps.merchantOrigin,
            referrerURL: deps.merchantOrigin,
            extensions,
            disclosures,
            neuroIdTokens: {
              neuroUserId: fraudAlloyContext?.neuroUserId || '',
              neuroIdSiteId: fraudAlloyContext?.neuroIdSiteId || '',
            },
            decibelSessionId,
            parentApplicationId: input.context.application?.id,
          },
          deps.apiFetch
        );
        return { applicationRes };
      } catch (error) {
        throw { error };
      }
    }
  );

type ReCreateApplicationDeps = {
  apiFetch: APIFetch;
  order: Order;
  locale: Locale;
  merchantOrigin: string;
  locationID: string | null;
  cartID: string | null;
  program: Program;
};

/**
 * Factory for `reCreateApplication`. Requires the same dependencies as
 * `createApplication` because it falls back to the captured order when the
 * event-supplied order is missing.
 * @param deps
 * @returns actor that recreates an application
 */
export const createReCreateApplication = (
  deps: ReCreateApplicationDeps
): PromiseActorLogic<
  ReCreateApplicationResult,
  SendReCreateApplicationParams
> =>
  fromPromise<ReCreateApplicationResult, SendReCreateApplicationParams>(
    async ({ input }) => {
      const { resolve, reject, options } = input;

      try {
        const { order: optionsOrder } = options;
        const requestOrder = optionsOrder || deps.order;

        if (!requestOrder) {
          throw new Error('Cannot create an application without an order!');
        }

        const fraudAlloyMachineActorRef =
          input.context.stateMachineActorRefs.fraudAlloyMachineActorRef;

        const fraudAlloyContext =
          fraudAlloyMachineActorRef?.getSnapshot().context;

        const decibelSessionId =
          window.decibelInsight?.<string>('getSessionId');

        const disclosures: DisclosuresAttribute =
          deps.program.policies.application.tof.disclosures?.map(
            (disclosure) => ({
              type: disclosure.type,
            })
          ) || [];

        const extensions: Application['extensions'] = [
          ...(deps.locationID && deps.locationID !== ZERO_UUID
            ? [{ key: 'locationID', value: deps.locationID }]
            : []),
          ...(deps.cartID ? [{ key: 'cartID', value: deps.cartID }] : []),
        ];

        const { items } = requestOrder;

        const applicationRes = await createApplicationHandler(
          {
            order: {
              ...requestOrder,
              items: items
                ? stringifyShippingDescription(items, deps.locale)
                : [],
            },
            retargetingURL: deps.merchantOrigin,
            referrerURL: deps.merchantOrigin,
            extensions,
            disclosures,
            neuroIdTokens: {
              neuroUserId: fraudAlloyContext?.neuroUserId || '',
              neuroIdSiteId: fraudAlloyContext?.neuroIdSiteId || '',
            },
            decibelSessionId,
            parentApplicationId: input.context.application?.id,
          },
          deps.apiFetch
        );
        return { applicationRes, resolve, reject };
      } catch (error) {
        throw { error, reject };
      }
    }
  );

type ApplicationCheckoutDeps = {
  apiFetch: APIFetch;
  order: Order;
};

/**
 * Factory for `applicationCheckout`. Requires the API-fetch wrapper and the
 * current order (for pickup information).
 * @param deps
 * @returns actor that submits the application checkout
 */
export const createApplicationCheckout = (
  deps: ApplicationCheckoutDeps
): PromiseActorLogic<
  ApplicationCheckoutResult,
  SendApplicationCheckoutParams
> =>
  fromPromise<ApplicationCheckoutResult, SendApplicationCheckoutParams>(
    async ({ input }) => {
      const { resolve, reject, options, context: ctx } = input;

      try {
        const {
          application,
          generatedDisclosures,
          selectedOffer,
          stateMachineActorRefs,
        } = ctx;

        const { fraudAlloyMachineActorRef } = stateMachineActorRefs;
        const iovationBlackboxId =
          fraudAlloyMachineActorRef?.getSnapshot().context.iovationBlackboxId;

        const {
          buyer,
          shippingContactID,
          paymentMethodID,
          autoPaymentMethodID,
        } = options;
        const { pickupInformation } = deps.order;

        if (!application || !selectedOffer || !generatedDisclosures) {
          throw new Error(
            'Application, offer, and generated disclosures required for application checkout!'
          );
        }

        const generatedDisclosureResults: GeneratedDisclosures[] = [
          generatedDisclosures,
        ];
        if (autoPaymentMethodID) {
          const generatedDisclosureResult = await generateDisclosures({
            paymentAgreementID: selectedOffer.paymentAgreement.id,
            disclosureOperation: DisclosureOperation.AUTOPAY_ENROLLMENT,
            paymentMethodID: autoPaymentMethodID,
            apiFetch: deps.apiFetch,
          });

          if (generatedDisclosureResult.error) {
            throw new Error(
              'Error generating autopay disclosures: ' +
                generatedDisclosureResult.error.message
            );
          }
          generatedDisclosureResults.push(generatedDisclosureResult.result);
        }

        const disclosuresWithChecksum = getRequiredDisclosureChecksums(
          selectedOffer,
          generatedDisclosureResults
        );

        const decibelSessionId =
          window.decibelInsight?.<string>('getSessionId');

        const applicationRes = await applicationCheckoutHandler(
          {
            application: {
              ...application,
              order: {
                ...application.order,
                ...(pickupInformation && {
                  pickupInformation: {
                    ...pickupInformation,
                    name: pickupInformation?.name || buyer?.identity.name,
                    email: pickupInformation?.email || buyer?.identity.email,
                    phone: pickupInformation?.phone || buyer?.identity.phone,
                  },
                }),
              },
            },
            shippingContactID,
            disclosures: disclosuresWithChecksum,
            selectedOffer,
            iovationBlackboxId,
            paymentMethodID,
            autoPaymentMethodID,
            decibelSessionId,
          },
          deps.apiFetch
        );

        return { applicationRes, resolve, reject };
      } catch (error) {
        throw { error, reject };
      }
    }
  );

type ApplicationPrepareCheckoutDeps = {
  apiFetch: APIFetch;
  merchantPaymentProducts: MerchantPaymentProduct[];
};

/**
 * Factory for `applicationPrepareCheckout`. Requires the API-fetch wrapper
 * and the merchant payment products used to validate returned offers.
 * @param deps
 * @returns actor that prepares the application checkout
 */
export const createApplicationPrepareCheckout = (
  deps: ApplicationPrepareCheckoutDeps
): PromiseActorLogic<
  ApplicationPrepareCheckoutResult,
  SendApplicationPrepareCheckoutParams
> =>
  fromPromise<
    ApplicationPrepareCheckoutResult,
    SendApplicationPrepareCheckoutParams
  >(async ({ input }) => {
    const { resolve, reject, options, context: ctx } = input;
    try {
      const {
        application,
        generatedDisclosures,
        selectedOffer,
        stateMachineActorRefs,
      } = ctx;

      const { fraudAlloyMachineActorRef } = stateMachineActorRefs;
      const iovationBlackboxId =
        fraudAlloyMachineActorRef?.getSnapshot().context.iovationBlackboxId;

      const { paymentMethodID } = options;
      const decibelSessionId = window.decibelInsight?.<string>('getSessionId');

      if (!application || !selectedOffer || !generatedDisclosures) {
        throw new Error(
          'Application, offer, and generated disclosures required for application checkout!'
        );
      }

      const disclosuresWithChecksum = getRequiredDisclosureChecksums(
        selectedOffer,
        [generatedDisclosures]
      );

      const applicationRes = await applicationPrepareCheckoutHandler(
        {
          application,
          disclosures: disclosuresWithChecksum,
          iovationBlackboxId,
          paymentMethodID,
          selectedOffer,
          decibelSessionId,
        },
        deps.apiFetch
      );

      const appResult = applicationRes.result;

      // Make sure the offers for new estimated spend order values are valid
      if (appResult) {
        const validatedOffers = validateOfferProductTypes(
          appResult?.offers || [],
          deps.merchantPaymentProducts
        );

        const applicationResWithValidatedOffers = toResultResponse<Application>(
          {
            ...appResult,
            offers: validatedOffers,
          }
        );
        return {
          applicationRes: applicationResWithValidatedOffers,
          resolve,
          reject,
        };
      }

      return { applicationRes, resolve, reject };
    } catch (error) {
      throw { error, reject };
    }
  });

/**
 * Actor that prepares an in-store application by fetching the payment method
 * @returns actor that prepares the in-store application by fetching the payment method
 */
export const prepareInStoreApplication = fromPromise<
  PrepareInStoreApplicationResult,
  ApplicationContext
>(async ({ input }) => {
  const app = input.application;
  const paymentsMachineActorRef =
    input.stateMachineActorRefs?.paymentsMachineActorRef;

  if (!app) {
    throw new Error('Missing context while preparing application!');
  }

  if (!paymentsMachineActorRef) {
    throw new Error('Missing payments machine actor ref in context!');
  }

  if (app.paymentMethodID) {
    await new Promise((resolve, reject) => {
      paymentsMachineActorRef.send({
        type: 'SEND_FETCH_PAYMENT_METHOD',
        params: {
          paymentMethodID: app.paymentMethodID || '',
          buyerID: app.buyerID || '',
          resolve,
          reject,
        },
      });
    });
  }

  const applicationRes = toResultResponse<Application>(app);

  return { applicationRes };
});

type PrepareVirtualCardIssuanceDeps = {
  apiFetch: APIFetch;
  setVirtualCardIssuance: (issuance: VirtualCardIssuanceResponse) => void;
};

/**
 * Factory for `prepareVirtualCardIssuance`. Requires the API-fetch wrapper
 * and the AppData setter for the issued virtual card.
 * @param deps
 * @returns actor that polls and stores the virtual card issuance
 */
export const createPrepareVirtualCardIssuance = (
  deps: PrepareVirtualCardIssuanceDeps
): PromiseActorLogic<PrepareVirtualCardIssuanceResult, ApplicationContext> =>
  fromPromise<PrepareVirtualCardIssuanceResult, ApplicationContext>(
    async ({ input }) => {
      const app = input.application;
      if (!app) {
        throw new Error(
          'Cannot prepare virtual issuance without an application!'
        );
      }
      const issuanceResponse = await pollVirtualCardIssuance(
        `APPLICATION.${app.id}`,
        deps.apiFetch
      );

      if (
        issuanceResponse.error ||
        issuanceResponse.result.issuance.status !== 'ISSUED' ||
        issuanceResponse.result.card?.status === 'FAILED'
      ) {
        throw new Error(
          'Virtual Card Error: Error obtaining virtual card issuance.'
        );
      }

      deps.setVirtualCardIssuance(issuanceResponse.result);

      return { issuanceResponse };
    }
  );

type LocateExchangedOfferAndGenerateDisclosuresDeps = {
  apiFetch: APIFetch;
  paymentAgreementID: string | undefined;
  namespace: NamespacePath;
};

/**
 * Factory for `locateExchangedOfferAndGenerateDisclosures`. Requires the
 * API-fetch wrapper, the exchanged payment-agreement ID to look up, and the
 * active namespace.
 * @param deps
 * @returns actor that locates the exchanged offer and generates disclosures
 */
export const createLocateExchangedOfferAndGenerateDisclosures = (
  deps: LocateExchangedOfferAndGenerateDisclosuresDeps
): PromiseActorLogic<
  LocateExchangedOfferAndGenerateDisclosuresResult,
  ApplicationContext
> =>
  fromPromise<
    LocateExchangedOfferAndGenerateDisclosuresResult,
    ApplicationContext
  >(async ({ input }) => {
    const app = input.application;
    try {
      if (!app) {
        throw new Error(
          'Cannot generate exchanged disclosures without an application!'
        );
      }

      let selectedOffer = app.offers?.find(
        (o) => o.paymentAgreement.id === deps.paymentAgreementID
      );

      // if we can't find the offer on the current application, search
      // for other applications that may provide a match
      if (!selectedOffer) {
        const latestApplications = await getLatestApplications(
          5,
          deps.apiFetch
        );

        for (const a of latestApplications) {
          selectedOffer = a.offers?.find(
            (o) => o.paymentAgreement.id === deps.paymentAgreementID
          );
          if (selectedOffer) {
            break;
          }
        }

        // Try to find the corresponding offer on the original application
        // if we found an offer match on another application via paymentProductID
        selectedOffer = app.offers?.find(
          (o) => o.paymentProduct.id === selectedOffer?.paymentProduct.id
        );
      }

      // If we still can't find the offer, throw an error. Take buyer to terms
      if (!selectedOffer) {
        throw new Error(
          'Cannot generate exchanged disclosures without a selected offer!'
        );
      }

      const generatedDisclosuresResponse = await generateDisclosures({
        paymentAgreementID: selectedOffer.paymentAgreement.id ?? '',
        disclosureOperation: DisclosureOperation.CHECKOUT,
        apiFetch: deps.apiFetch,
      });

      if (generatedDisclosuresResponse.error) {
        throw new Error(
          'Error generating disclosures for exchanged offer: ' +
            generatedDisclosuresResponse.error.message
        );
      }

      return {
        generatedDisclosures: generatedDisclosuresResponse.result,
        selectedOffer,
      };
    } catch (error) {
      throw { error };
    }
  });

type ReCreateInStoreContingentApplicationDeps = {
  apiFetch: APIFetch;
  locale: Locale;
  merchantOrigin: string;
  locationID: string | null;
  cartID: string | null;
  program: Program;
};

/**
 * Factory for `reCreateInStoreContingentApplication`. Requires the same
 * disclosure-generation dependencies as `createApplication` but uses the
 * upstream actor result's capacity to compute the recreated order.
 * @param deps
 * @returns actor that recreates an in-store contingent application
 */
export const createReCreateInStoreContingentApplication = (
  deps: ReCreateInStoreContingentApplicationDeps
): PromiseActorLogic<
  ReCreateInStoreContingentApplicationResult,
  SendReCreateContingentInStoreApplicationParams
> =>
  fromPromise<
    ReCreateInStoreContingentApplicationResult,
    SendReCreateContingentInStoreApplicationParams
  >(async ({ input }) => {
    try {
      const app = input.actorResult.applicationRes.result;

      if (!app) {
        throw new Error(
          'Cannot recreate a contingent application without an existing application!'
        );
      }

      const updatedOrder: Order = {
        items: [],
        subTotal: app.capacity,
        totalDiscounts: usd(0),
        totalPrice: app.capacity,
        totalShipping: usd(0),
        totalTax: usd(0),
      };

      const { items } = updatedOrder;

      const disclosures: DisclosuresAttribute =
        deps.program.policies.application.tof.disclosures?.map(
          (disclosure) => ({
            type: disclosure.type,
          })
        ) || [];

      const extensions: Application['extensions'] = [
        ...(deps.locationID && deps.locationID !== ZERO_UUID
          ? [{ key: 'locationID', value: deps.locationID }]
          : []),
        ...(deps.cartID ? [{ key: 'cartID', value: deps.cartID }] : []),
      ];

      const fraudAlloyMachineActorRef =
        input.context.stateMachineActorRefs.fraudAlloyMachineActorRef;

      const fraudAlloyContext =
        fraudAlloyMachineActorRef?.getSnapshot().context;

      const decibelSessionId = window.decibelInsight?.<string>('getSessionId');

      const applicationRes = await createApplicationHandler(
        {
          order: {
            ...updatedOrder,
            items: items
              ? stringifyShippingDescription(items, deps.locale)
              : [],
          },
          retargetingURL: deps.merchantOrigin,
          referrerURL: deps.merchantOrigin,
          extensions,
          disclosures,
          neuroIdTokens: {
            neuroUserId: fraudAlloyContext?.neuroUserId || '',
            neuroIdSiteId: fraudAlloyContext?.neuroIdSiteId || '',
          },
          decibelSessionId,
          parentApplicationId: input.context.application?.id,
        },
        deps.apiFetch
      );

      return { applicationRes };
    } catch (error) {
      throw { error };
    }
  });
